/**
 * Progress Bar Component
 * Consistent progress indicators for uploads, processing, form completion
 * Consolidates LinearProgress usage
 */

import React from 'react';
import { styled } from '@mui/material/styles';
import { LinearProgress, Box, Typography } from '@mui/material';
import { loadingConfig } from './loadingConfig';

/**
 * Styled Linear Progress with consistent theming
 */
const StyledLinearProgress = styled(LinearProgress)(({ height, status }) => {
  const { colors, radius } = loadingConfig;

  // Determine bar color based on status
  let barColor = colors.progress.bar;
  if (status === 'success') barColor = colors.progress.success;
  if (status === 'error') barColor = colors.progress.error;

  return {
    height: height || loadingConfig.sizes.progress.normal,
    borderRadius: radius.progress,
    backgroundColor: colors.progress.track,

    '& .MuiLinearProgress-bar': {
      borderRadius: radius.progress,
      backgroundColor: barColor,
      transition: `all ${loadingConfig.animation.fadeIn.duration} ${loadingConfig.animation.fadeIn.easing}`,
    },
  };
});

/**
 * Progress Bar Component
 *
 * @param {Object} props
 * @param {number} props.value - Progress value (0-100)
 * @param {string} props.variant - 'determinate' | 'indeterminate'
 * @param {string} props.size - 'thin' | 'normal' | 'thick'
 * @param {string} props.status - 'default' | 'success' | 'error'
 * @param {boolean} props.showLabel - Show percentage label
 * @param {string} props.label - Custom label text
 * @param {Object} props.sx - Additional MUI sx styles
 */
export default function ProgressBar({
  value = 0,
  variant = 'determinate',
  size = 'normal',
  status = 'default',
  showLabel = false,
  label,
  sx = {},
  ...props
}) {
  const { sizes, a11y } = loadingConfig;
  const height = sizes.progress[size];

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <StyledLinearProgress
        variant={variant}
        value={value}
        height={height}
        status={status}
        aria-label={label || a11y.labels.loading}
        role="progressbar"
        aria-valuenow={variant === 'determinate' ? value : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        {...props}
      />

      {showLabel && variant === 'determinate' && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            marginTop: 1,
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          {label || `${Math.round(value)}%`}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Predefined progress bar variants
 */

/**
 * Upload Progress - For file uploads
 */
export function UploadProgress({ value, sx = {}, ...props }) {
  const { a11y } = loadingConfig;

  return (
    <ProgressBar
      value={value}
      size="normal"
      showLabel
      label={value === 100 ? a11y.labels.completed : `${Math.round(value)}%`}
      status={value === 100 ? 'success' : 'default'}
      sx={{
        marginTop: 2,
        ...sx,
      }}
      aria-label={a11y.labels.uploading}
      {...props}
    />
  );
}

/**
 * Form Completion Progress - For EnhancedProgress component
 */
export function FormCompletionProgress({ value, sx = {}, ...props }) {
  return (
    <ProgressBar
      value={value}
      size="thick"
      status={value === 100 ? 'success' : 'default'}
      sx={{
        // RTL support for Hebrew
        transform: 'scaleX(-1)',
        ...sx,
      }}
      {...props}
    />
  );
}

/**
 * Mobile Field Navigation Progress
 */
export function MobileNavigationProgress({ current, total, sx = {}, ...props }) {
  const percentage = (current / total) * 100;

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          marginBottom: 1,
          fontSize: '12px',
          textAlign: 'center',
        }}
      >
        שאלה {current} מתוך {total}
      </Typography>

      <ProgressBar value={percentage} size="thin" {...props} />
    </Box>
  );
}

/**
 * Auto-Save Progress - Subtle indicator
 */
export function AutoSaveProgress({ isSaving, sx = {}, ...props }) {
  return (
    <ProgressBar
      variant="indeterminate"
      size="thin"
      sx={{
        opacity: isSaving ? 1 : 0,
        transition: 'opacity 0.3s ease',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        ...sx,
      }}
      {...props}
    />
  );
}
